import React, { useState } from 'react';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import URLSearchParams from '@ungap/url-search-params';

import { Auth } from '@mdlinx/shared';
import {
  SignUpFormEmailOrSocial,
  SignUpFormDetailed,
  SignUpFormAdditionalInfo,
  SignUpFormAdditionalInfoProps,
  SignUpFormAcquisition,
  SignUpFormDetailedProps,
  SignUpFormAcquisitionProps,
  SignUpThankYou,
} from '../components/sign-up-forms';
import {
  RegisterUserError,
  useRegisterUserMutation,
  useUpdateUserProfileMutation,
  useMeQuery,
  useChangeEmailMutation,
  UpdateUserProfileInput,
  useRegisterAcquisitionUserMutation,
  RegisterAcquisitionUserError,
} from 'generated/client';

import { SocialAccountForm, SocialPopupOverlay, ProviderUserProfile } from './SocialAccountForm';
import { extractDataFromResponse, useErrorReporter } from '@mdlinx/frontend-shared';

import { translateChangeEmailError } from './ChangeEmail';
import { trackEvent, getLocationDetails } from 'lib/analytics';
import { messages } from 'lib/messages';
import { RegistrationTrackingData } from 'lib/tracking';

const WAIT_MS_AFTER_REGISTRATION = 1000;
async function wait(durationMs: number) {
  return new Promise(resolve => {
    setTimeout(resolve, durationMs);
  });
}

function translateRegisterUserError(error: RegisterUserError): string {
  switch (error) {
    case RegisterUserError.EmailAlreadyExists:
      return messages.error.signUp.emailAlreadyExists;
    case RegisterUserError.EmailInvalid:
      return messages.error.signUp.emailInvalid;
    case RegisterUserError.PostalCodeRequired:
      return messages.error.signUp.postalCodeRequired;
    default:
      return 'register user error';
  }
}

const getCopyForSignUp = (
  appName: 'smartestDoc' | 'mdlinx',
  type: 'acquisition' | 'acquitisition-with-personalization' | 'sign-up',
): { header: string; subHeader: string } => {
  switch (true) {
    case appName === 'smartestDoc' && type === 'acquisition':
    case appName === 'smartestDoc' && type === 'acquitisition-with-personalization':
    case appName === 'smartestDoc' && type === 'sign-up':
      return {
        header: 'Sign up to keep playing',
        subHeader: 'Join MDLinx to continue playing Smartest Doc and reading great content.',
      };

    case appName === 'mdlinx' && type === 'acquisition':
      return {
        header: 'Sign up to keep reading',
        subHeader: 'Join MDLinx to read this and other great content.',
      };

    case appName === 'mdlinx' && type === 'acquitisition-with-personalization':
      return {
        header: 'Sign up to keep reading',
        subHeader: 'Sign up for free to continue reading this article.',
      };
    default:
      return {
        header: 'Sign up to keep reading',
        subHeader: 'Sign up for free to continue reading this article and more from MDLinx.',
      };
  }
};

interface SignUpProps {
  onSocialSignUp: (providerUserProfile?: ProviderUserProfile) => void;
  initialProfileValues: {
    firstName: string;
    lastName: string;
    email: string;
    postalCode: string;
    selfReportedUsmd: boolean;
  };
  registrationTrackingData: RegistrationTrackingData;
}

export const SignUp = ({ onSocialSignUp, initialProfileValues, registrationTrackingData }: SignUpProps) => {
  const [error, setError] = useState('');
  const [isOverlayVisible, setOverlay] = useState(false);
  const [registerUser] = useRegisterUserMutation();
  const [registerAcquisitionUser] = useRegisterAcquisitionUserMutation();
  const [updateUserProfile] = useUpdateUserProfileMutation({ errorPolicy: 'all' });
  const [changeEmail] = useChangeEmailMutation();
  const errorReporter = useErrorReporter();
  const meResponse = useMeQuery({ fetchPolicy: 'network-only' });
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const parentUrl = params.get('parentUrl') ?? '';
  const isSmartestDoc = parentUrl.includes('sdbeta') || parentUrl.includes('smdoc');
  function postMessage(message: Auth.LoginResult) {
    if (window.parent) {
      window.parent.postMessage(message, '*');
    }
  }

  const onSubmit: SignUpFormDetailedProps['onSubmit'] = (values, actions) => {
    trackEvent('auth_click', { action: 'sign up', ...getLocationDetails(location.pathname, location.search) });
    setError(''); // clear error message on every submit
    return registerUser({
      variables: {
        input: values,
        registrationTrackingData,
      },
    })
      .then(extractDataFromResponse)
      .then(async ({ registerUser }) => {
        switch (registerUser.__typename) {
          case 'RegisterUserSuccess':
            trackEvent('auth_sign_up_succeeded', {
              action: 'onSubmit',
              ...getLocationDetails(location.pathname, location.search),
            });

            actions.setSubmitting(false);
            if (!registerUser.userCanBeVerified && values.selfReportedUsmd) {
              return history.push('/sign-up/additional-questions');
            }

            // For GA goal tracking
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push({
              event: 'auth-fe-organic-registration-complete',
            });

            await wait(WAIT_MS_AFTER_REGISTRATION);

            return postMessage(Auth.loginSucceeded);
          case 'RegisterUserFailure':
            trackEvent('auth_sign_up_failed', {
              action: 'onSubmit',
              ...getLocationDetails(location.pathname, location.search),
              error: registerUser.error,
            });
            actions.setSubmitting(false);
            return setError(translateRegisterUserError(registerUser.error));
          default:
            throw new Error(`__typename should be specified. ${JSON.stringify({ registerUser })}`);
        }
      })
      .catch(errorReporter.report);
  };

  const onSubmitAdditionalInformation: SignUpFormAdditionalInfoProps['onSubmit'] = (values, actions) => {
    trackEvent('auth_click', {
      action: 'onSubmitAdditionalInformation',
      ...getLocationDetails(location.pathname, location.search),
    });
    const input = {
      medicalSchool: values.medicalSchool,
      graduationYear: Number(values.graduationYear),
      specialtyId: Number(values.specialtyId),
    };
    setError(''); // clear error message on every submit
    return handleUpdateUserProfile(input, actions.setSubmitting);
  };

  const handleDeclineSubmitMedicalSchool = () => {
    trackEvent('auth_click', {
      action: 'handleDeclineSubmitMedicalSchool',
      ...getLocationDetails(location.pathname, location.search),
    });
    return updateUserProfile({
      variables: {
        input: {
          selfReportedUsmd: false,
        },
      },
    })
      .then(async () => {
        // For GA goal tracking
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: 'auth-fe-organic-registration-complete',
        });

        await wait(WAIT_MS_AFTER_REGISTRATION);

        postMessage(Auth.loginSucceeded);
      })
      .catch(errorReporter.report);
  };

  const handleSubmitProfileCheckInfo: SignUpFormDetailedProps['onSubmit'] = async (values, actions) => {
    trackEvent('auth_click', {
      action: 'handleSubmitProfileCheckInfo',
      ...getLocationDetails(location.pathname, location.search),
    });

    const me = !meResponse.loading && meResponse.data && meResponse.data.me;
    const { email, ...restValues } = values;
    setError('');
    // check if the email from the check information form is different
    if ((me && me.account && me.account.email) !== values.email) {
      changeEmail({ variables: { input: { newEmail: values.email } } })
        .then(extractDataFromResponse)
        .then(async ({ changeEmail: result }) => {
          switch (result.__typename) {
            case 'ChangeEmailSuccess': {
              await handleUpdateUserProfile(restValues, actions.setSubmitting);
              actions.setSubmitting(false);
              return;
            }
            case 'ChangeEmailFailure': {
              actions.setSubmitting(false);
              return setError(translateChangeEmailError(result.error));
            }
          }
        }, errorReporter.report);
    } else {
      await handleUpdateUserProfile(restValues, actions.setSubmitting);
      actions.setSubmitting(false);
    }
  };

  const handleUpdateUserProfile = async (
    input: UpdateUserProfileInput,
    setSubmitting: (isSubmitting: boolean) => void,
  ) => {
    setError('');

    try {
      const res = await updateUserProfile({ variables: { input } });
      const data = extractDataFromResponse(res);
      setSubmitting(false);
      trackEvent('auth_update_profile_succeeded', {
        action: 'handleUpdateUserProfile',
        ...getLocationDetails(location.pathname, location.search),
      });

      if (location.pathname === '/sign-up/additional-questions') {
        // For GA goal tracking
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: 'auth-fe-organic-registration-complete',
        });

        await wait(WAIT_MS_AFTER_REGISTRATION);

        return history.push('/sign-up/thank-you');
      }

      if (!data.updateUserProfile.verifiedUsmd) {
        return history.push('/sign-up/additional-questions');
      }
    } catch (error) {
      setSubmitting(false);
      trackEvent('auth_update_profile_failed', {
        action: 'handleUpdateUserProfile',
        ...getLocationDetails(location.pathname, location.search),
      });
      setError(
        error.graphQLErrors.map(
          ({
            message,
            extensions,
          }: {
            message: string;
            extensions: { code: string; exception: { errors: string[] } };
          }) => {
            if (extensions) {
              return extensions.exception.errors.join('');
            }
            return message;
          },
        ),
      );
      errorReporter.report(error);
    }
  };

  const handleRegisterAcquisitionUser: SignUpFormAcquisitionProps['onSubmit'] = (values, actions) => {
    trackEvent('auth_click', { action: 'sign up', ...getLocationDetails(location.pathname, location.search) });

    setError(''); // clear error message on every submit
    return registerAcquisitionUser({
      variables: {
        input: values,
        registrationTrackingData,
      },
    })
      .then(extractDataFromResponse)
      .then(async ({ registerAcquisitionUser }) => {
        switch (registerAcquisitionUser.__typename) {
          case 'RegisterAcquisitionUserSuccess':
            // For GA goal tracking
            (window as any).dataLayer = (window as any).dataLayer || [];
            (window as any).dataLayer.push({
              event: 'auth-fe-acquisition-registration-complete',
            });

            await wait(WAIT_MS_AFTER_REGISTRATION);

            trackEvent('auth_register_acquisition_user_succeeded', {
              action: 'handleRegisterAcquisitionUser',
              ...getLocationDetails(location.pathname, location.search),
            });
            actions.setSubmitting(false);
            return postMessage(Auth.loginSucceeded);
          case 'RegisterAcquisitionUserFailure':
            actions.setSubmitting(false);
            trackEvent('auth_register_acquisition_user_failed', {
              action: 'handleRegisterAcquisitionUser',
              ...getLocationDetails(location.pathname, location.search),
            });

            if (registerAcquisitionUser.error === RegisterAcquisitionUserError.EmailAlreadyExists) {
              return history.push(`/login?acquisitionError=${registerAcquisitionUser.error}`);
            }

            if (registerAcquisitionUser.error === RegisterAcquisitionUserError.EmailInvalid) {
              setError(messages.error.acquisition.emailInvalid);
              return;
            }

            if (registerAcquisitionUser.error === RegisterAcquisitionUserError.AcquisitionParamsNotFound) {
              setError(messages.error.acquisition.acquisitionParamsNotFound);
              return history.push('/sign-up');
            }

            return setError(messages.error.acquisition.unknown);
          default:
            throw new Error(`__typename should be specified. ${JSON.stringify({ registerAcquisitionUser })}`);
        }
      })
      .catch(errorReporter.report);
  };

  const handleLoginLinkClicked = () => {
    trackEvent('auth_click', {
      action: 'onLoginLinkClicked',
      ...getLocationDetails(location.pathname, location.search),
    });
    history.push('/login');
  };

  return (
    <Switch>
      <Route
        path={'/sign-up/acquisition'}
        render={({ history }) => {
          let userName;
          const me = !meResponse.loading && meResponse.data && meResponse.data.me;
          let initialEmail = '';
          let header = getCopyForSignUp(isSmartestDoc ? 'smartestDoc' : 'mdlinx', 'acquisition').header;
          let subHeader = getCopyForSignUp(isSmartestDoc ? 'smartestDoc' : 'mdlinx', 'acquisition').subHeader;

          if (me && me.acquisitionProfile) {
            initialEmail = me.acquisitionProfile.email || '';
            userName = me.acquisitionProfile.lastName ? `Dr. ${me.acquisitionProfile.lastName}` : undefined;
            header = `Welcome ${userName}`;
            subHeader = getCopyForSignUp(isSmartestDoc ? 'smartestDoc' : 'mdlinx', 'acquitisition-with-personalization')
              .subHeader;
          }

          return (
            <SignUpFormAcquisition
              userName={userName}
              header={header}
              subHeader={subHeader}
              onSubmit={handleRegisterAcquisitionUser}
              errorMessage={error}
              onCreateAnAccountClick={() => {
                trackEvent('auth_click', {
                  action: 'onCreateAnAccountClick',
                  ...getLocationDetails(location.pathname, location.search),
                });
                history.push('/sign-up/detailed');
              }}
              initialEmail={initialEmail}
              onLoginLinkClicked={handleLoginLinkClicked}
            />
          );
        }}
      />

      <Route exact path={'/sign-up/'}>
        <SocialPopupOverlay isOverlayVisible={isOverlayVisible} />
        <SignUpFormEmailOrSocial
          header={getCopyForSignUp(isSmartestDoc ? 'smartestDoc' : 'mdlinx', 'sign-up').header}
          subHeader={getCopyForSignUp(isSmartestDoc ? 'smartestDoc' : 'mdlinx', 'sign-up').subHeader}
          errorMessage={error}
          socialButtons={
            <SocialAccountForm
              registrationTrackingData={registrationTrackingData}
              onSocialLogin={() => postMessage(Auth.loginSucceeded)}
              onSocialSignUp={onSocialSignUp}
              setLoading={setOverlay}
              isSignUp
            />
          }
          onClickEmailSignUp={() => {
            trackEvent('auth_click', {
              action: 'onClickEmailSignUp',
              ...getLocationDetails(location.pathname, location.search),
            });
            history.push('/sign-up/detailed');
          }}
          onLoginLinkClicked={handleLoginLinkClicked}
        />
      </Route>
      <Route path="/sign-up/check-info">
        <SignUpFormDetailed
          header="Check your information"
          subHeader="Please confirm your information to help personalize your MDLinx experience."
          onSubmit={handleSubmitProfileCheckInfo}
          errorMessage={error}
          initialFormValues={initialProfileValues}
          onLoginLinkClicked={handleLoginLinkClicked}
        />
      </Route>

      <Route path="/sign-up/detailed">
        <SignUpFormDetailed
          header="Sign up"
          subHeader="Please provide the following information."
          onSubmit={onSubmit}
          errorMessage={error}
          onLoginLinkClicked={handleLoginLinkClicked}
        />
      </Route>

      <Route path="/sign-up/detailed-acquisition">
        <SignUpFormDetailed
          header="Check your information"
          subHeader="Please confirm your information for a personalize your MDLinx experience."
          onSubmit={onSubmit}
          errorMessage={error}
          onLoginLinkClicked={handleLoginLinkClicked}
        />
      </Route>
      <Route path="/sign-up/additional-questions">
        <SignUpFormAdditionalInfo
          onSubmit={onSubmitAdditionalInformation}
          errorMessage={error}
          onDeclineSubmitMedicalSchool={handleDeclineSubmitMedicalSchool}
        />
      </Route>
      <Route path="/sign-up/thank-you">
        <SignUpThankYou
          thankYouText={`Thank you for updating your credentials.`}
          textAlignment="left"
          onCloseLinkClicked={() => {
            postMessage(Auth.loginSucceeded);
          }}
        />
      </Route>
    </Switch>
  );
};
